// Shared home/task reads use established workspace query keys; mutations refresh the original source workflows.
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { defaultRange } from '@/lib/insightsRange';
import type { InsightsSummaryResult } from '@/lib/insightsTypes';
import type { SavingsDetail } from '@/components/savings/shared';
import { getSavingsTasks, getTaskOverview } from '@/lib/taskOverview';
import { useWorkspaceStore } from '@/stores/workspace';

export interface HomeUpcomingItem { id: string; kind: 'expense' | 'income'; name: string; amount_aud: number; next_due_date: string; days_until: number }
interface HomeUpcomingResponse { today: string; items: HomeUpcomingItem[]; expense_count: number; income_count: number }
interface HomeAlertsResponse {
  today: string;
  alerts: Array<{ id: string; alert_type: string; title: string; severity: string; action_url: string | null }>;
  counts: { total: number; critical: number; warn: number; info: number; celebrate: number };
}
export function useHomeAlerts() {
  const ws = useWorkspaceStore(state => state.current);
  return useQuery({ queryKey: ['alerts', ws?.id], queryFn: () => api.get<HomeAlertsResponse>('/alerts'), enabled: !!ws, staleTime: 60_000 });
}
export function useHouseholdTasks() {
  const ws = useWorkspaceStore(state => state.current);
  const alertsQuery = useHomeAlerts();
  const upcomingQuery = useQuery({ queryKey: ['insights', 'upcoming-payments', ws?.id, 30], queryFn: () => api.get<HomeUpcomingResponse>('/insights/upcoming-payments?limit=30'), enabled: !!ws, staleTime: 30_000 });
  // Existence only: server filtering precedes LIMIT. Never infer a total receipt count.
  const pendingReceiptsQuery = useQuery({ queryKey: ['receipts-v2', ws?.id, 'home-pending-exists'], queryFn: () => api.get<Array<{ id: string }>>('/receipts?status=pending&limit=1'), enabled: !!ws, staleTime: 30_000 });
  const savingsQuery = useQuery({ queryKey: ['savings-plans', ws?.id], queryFn: () => api.get<{ plans: SavingsDetail[]; today: string }>('/savings-plans'), enabled: !!ws, staleTime: 0 });
  const duePlans = getSavingsTasks(savingsQuery.data);
  const overview = getTaskOverview({
    receipts: { status: pendingReceiptsQuery.status, hasItems: !!pendingReceiptsQuery.data?.length },
    schedule: { status: upcomingQuery.status, hasItems: !!upcomingQuery.data?.items.length },
    savings: { status: savingsQuery.status, hasItems: duePlans.length > 0 },
    alerts: { status: alertsQuery.status, types: alertsQuery.data?.alerts.map(alert => alert.alert_type) ?? [] },
  });
  const retryFailed = () => Promise.all([pendingReceiptsQuery, upcomingQuery, savingsQuery, alertsQuery].filter(query => query.isError).map(query => query.refetch()));
  return { alertsQuery, upcomingQuery, pendingReceiptsQuery, savingsQuery, duePlans, overview, retryFailed };
}
export function useHomeOverview() {
  const ws = useWorkspaceStore(state => state.current);
  const range = defaultRange();
  const summaryQuery = useQuery({
    queryKey: ['insights', 'summary', ws?.id, range.from, range.to, ''],
    queryFn: () => api.get<InsightsSummaryResult>(`/insights/summary?${new URLSearchParams({ from: range.from, to: range.to, compare: 'auto' })}`),
    enabled: !!ws, staleTime: 60_000,
  });
  return { summaryQuery, range, ...useHouseholdTasks() };
}
